import { z } from "zod";
import {
    AmountRoundingLevel,
    RuleActionType,
    RuleConditionType,
    RuleGroupType,
    RuleSource,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { dbIntIdSchema } from "@common/schemas/shared.schema";

export const RuleConditionObjectSchema = z.object({
    before: z.string().optional(),
    after: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
});

export const AmountRoundingValueSchema = z.object({
    level: z.nativeEnum(AmountRoundingLevel),
    isCredit: z.boolean(),
});

// Split-keyed SIMILAR_TO value (D2). A bare string stays legacy (whole cluster);
// this object variant restricts matching to a single effective split key
// (effectiveSplitKey null = plain/non-split rows only).
export const SimilarToValueSchema = z
    .object({
        normalizedDescriptionId: z.string(),
        effectiveSplitKey: z.string().nullable(),
    })
    .strict();

const AMOUNT_ROUNDING_CONDITION_TYPES = new Set<RuleConditionType>([
    RuleConditionType.AMOUNT_IS_ROUND,
    RuleConditionType.AMOUNT_IS_NOT_ROUND,
]);

export const RuleCreationConditionPayloadSchema = z
    .object({
        conditionType: z
            .nativeEnum(RuleConditionType)
            .describe(
                "Type of rule condition, such as description_includes, counterparty, category, amount, date, financial_accounts, entity, or class.",
            ),
        value: z
            .union([
                z.string(),
                z.array(z.string()),
                z.number(),
                z.array(z.number()),
                AmountRoundingValueSchema.strict(),
                SimilarToValueSchema,
                RuleConditionObjectSchema,
            ])
            .describe(
                "Condition value. UUID (or UUID[]) for category, class, counterparty, and subaccounts, plus a normalized-description UUID for similar_to (from the normalized_descriptions tool). Numeric id (not a UUID) for financial_accounts and entity. Text for description conditions; amount/date objects for ranged conditions. Resolve ids via the matching search/list tools first.",
            ),
        transferDirection: z
            .nativeEnum(TransferRuleDirection)
            .nullable()
            .describe(
                "Transfer direction for transfer rules, or null for non-transfer rule conditions.",
            ),
    })
    .superRefine((val, ctx) => {
        if (AMOUNT_ROUNDING_CONDITION_TYPES.has(val.conditionType)) {
            const parsed = AmountRoundingValueSchema.safeParse(val.value);
            if (!parsed.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["value"],
                    message: `value must be { level: ${Object.values(AmountRoundingLevel).join(" | ")}, isCredit: boolean }`,
                });
            }
        }
    });

export const RuleCreationActionPayloadSchema = z.object({
    actionType: z
        .nativeEnum(RuleActionType)
        .describe(
            "Action the rule applies, such as category, entity, class, counterparty, memo, account_id, or split.",
        ),
    value: z
        .union([z.string(), z.number(), z.array(z.string())])
        .describe(
            "Action value. Use UUIDv7 IDs from search/list tools for category, class, counterparty, or account actions; use numeric entity ids for entity actions; use text for memo actions.",
        ),
    transferDirection: z
        .nativeEnum(TransferRuleDirection)
        .nullable()
        .describe(
            "Transfer direction for transfer matching actions, or null for ordinary transaction actions.",
        ),
});

export const SplitConfigSchema = z
    .object({
        isPercentage: z.boolean().optional(),
        autoConfirm: z.boolean().optional(),
        splits: z
            .array(
                z.object({
                    categoryId: z.string().optional(),
                    amount: z.number().optional(),
                    entityId: z.number().optional(),
                    splitDescription: z.string().optional(),
                    classIds: z.array(z.string()).optional(),
                    counterpartyId: z.string().nullable().optional(),
                    accountId: z.string().uuid().nullable().optional(),
                    accrualAccountId: z.string().uuid().nullable().optional(),
                    scheduleTemplateId: z.string().uuid().nullable().optional(),
                }),
            )
            .min(2),
    })
    .superRefine((val, ctx) => {
        const isPercentage = !!val.isPercentage;
        const amounts = val.splits.map((s) => s.amount);

        if (isPercentage) {
            const undefinedIdx = amounts.findIndex((a) => a === undefined);
            if (undefinedIdx !== -1) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["splits", undefinedIdx, "amount"],
                    message:
                        "Percentage cannot be zero for any part of the split",
                });
                return;
            }

            const sum = (amounts as number[]).reduce((acc, a) => acc + a, 0);
            const EPS = 1e-6;
            if (Math.abs(sum - 100) > EPS) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["splits"],
                    message: `Split percentage must total 100%`,
                });
            }
        } else {
            const undefinedCount = amounts.filter(
                (a) => a === undefined,
            ).length;
            if (undefinedCount !== 1) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["splits"],
                    message:
                        "Exactly one split amount must be Net when fixed amount based",
                });
            }
        }
    });

// Lock-safety needs only the action TYPE — not its value/transferDirection.
// A dedicated schema (instead of RuleCreationActionPayloadSchema, whose `value`
// is required) accepts both the form's full draft actions (extra keys stripped)
// and the similar-tx drawer's `{ actionType }`-only payload.
export const MatchingTransactionsActionSchema = z.object({
    actionType: z.nativeEnum(RuleActionType),
});

export const MatchingTransactionsQuerySchema = z.object({
    groupType: z.nativeEnum(RuleGroupType),
    conditions: z.array(RuleCreationConditionPayloadSchema),
    splitActionConfig: SplitConfigSchema.optional(),
    page: z.number(),
    limit: z.number(),
    excludeSplitTransactions: z.boolean().optional(),
    // Draft actions (form) used to compute lock-safety for the preview. When
    // absent and no `ruleId` resolves, the preview is conservatively
    // non-lock-safe (locked rows excluded) so it never over-counts vs apply.
    actions: z.array(MatchingTransactionsActionSchema).optional(),
    // Saved-rule id (drawer) — the BE derives lock-safety from the saved rule.
    ruleId: z.string().uuid().optional(),
});

export const MatchingTransferTransactionsQueryBaseSchema = z.object({
    conditions: z.array(RuleCreationConditionPayloadSchema),
    page: z.number().int().min(0),
    limit: z.number().int().min(1).max(200),
    excludeMatchedTransfers: z.boolean(),
    excludedTransactionsIds: z.array(z.number()).optional(),
    matchingMode: z.enum(["allow", "disallow"]).optional(),
});

export const requireDateProximityForAllowMode = (
    val: {
        matchingMode?: "allow" | "disallow";
        conditions: Array<{ conditionType: RuleConditionType }>;
    },
    ctx: z.RefinementCtx,
) => {
    if (val.matchingMode === "disallow") {
        return;
    }
    const hasDateProximity = val.conditions.some(
        (condition) =>
            condition.conditionType === RuleConditionType.DATE_PROXIMITY,
    );
    if (!hasDateProximity) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["conditions"],
            message:
                'A date_proximity condition is required for transfer matching: { conditionType: "date_proximity", value: <number of days, e.g. 4>, transferDirection: null }.',
        });
    }
};

export const MatchingTransferTransactionsQuerySchema =
    MatchingTransferTransactionsQueryBaseSchema.superRefine(
        requireDateProximityForAllowMode,
    );

// Numeric-id conditions carry database int4 ids (not UUIDs). Their values are
// shape-guarded with dbIntIdSchema so a UUID / fraction / out-of-range / boolean
// id is rejected at validation (usage/400) rather than reaching a bad-int cast
// (500) or a query that silently over-matches. SUBACCOUNTS values are UUIDs, so
// they are not part of this numeric guard.
const NUMERIC_ID_CONDITION_TYPES = new Set<RuleConditionType>([
    RuleConditionType.ENTITY,
    RuleConditionType.FINANCIAL_ACCOUNTS,
]);

const ruleShapeGuard = (
    rule: {
        conditions?: Array<z.infer<typeof RuleCreationConditionPayloadSchema>>;
        actions?: Array<z.infer<typeof RuleCreationActionPayloadSchema>>;
    },
    ctx: z.RefinementCtx,
) => {
    const seen = new Set<string>();
    for (const condition of rule.conditions ?? []) {
        if (!NUMERIC_ID_CONDITION_TYPES.has(condition.conditionType)) {
            continue;
        }

        const values = Array.isArray(condition.value)
            ? condition.value
            : [condition.value];
        if (values.some((value) => !dbIntIdSchema.safeParse(value).success)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["conditions"],
                message: `${condition.conditionType} condition value must be a positive integer id.`,
            });
        }

        // Transfer rules legitimately carry FROM+TO of both types, so
        // uniqueness is per (conditionType, transferDirection), not per type.
        const key = `${condition.conditionType}:${condition.transferDirection ?? "null"}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["conditions"],
                message: `Duplicate ${condition.conditionType} condition for the same transferDirection.`,
            });
        }
        seen.add(key);
    }

    for (const action of rule.actions ?? []) {
        if (
            action.actionType === RuleActionType.ENTITY &&
            !dbIntIdSchema.safeParse(action.value).success
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["actions"],
                message: "entity action value must be a positive integer id.",
            });
        }
    }
};

export const RuleUpdateSchema = z
    .object({
        groupId: z
            .string()
            .uuid()
            .describe("Rule group UUIDv7 from rules_query.")
            .optional(),
        order: z.coerce
            .number()
            .nonnegative()
            .describe("Absolute rule order within the target group.")
            .optional(),
        conditions: z
            .array(RuleCreationConditionPayloadSchema)
            .describe(
                "Ordered conditions that must match for the rule to apply.",
            )
            .optional(),
        actions: z
            .array(RuleCreationActionPayloadSchema)
            .describe("Actions applied when all rule conditions match.")
            .optional(),
        isDisabled: z
            .boolean()
            .describe("Set true to disable the rule without deleting it.")
            .optional(),
        note: z
            .string()
            .max(5000)
            .nullable()
            .describe("Internal rule note. Use null to clear.")
            .optional(),
    })
    .superRefine(ruleShapeGuard);

export const UpdateRuleNoteSchema = z.object({
    note: z
        .string()
        .max(5000)
        .nullable()
        .describe("Internal rule note text, or null to clear the note."),
});

const RuleExistingTransactionApplicationSchema = z.object({
    applyToExistingTransactions: z
        .boolean()
        .describe(
            "Whether to apply the rule to existing matching transactions after confirmation.",
        ),
    excludedTransactionsIds: z
        .array(z.number())
        .describe(
            "Numeric transaction ids to exclude from retroactive application.",
        )
        .optional(),
    includedTransactionsIds: z
        .array(z.number())
        .describe(
            "Numeric transaction ids to include explicitly for retroactive application.",
        )
        .optional(),
    excludeMatchedTransfers: z
        .boolean()
        .describe("Set true to skip already matched transfer transactions.")
        .optional(),
    excludeSplitTransactions: z
        .boolean()
        .describe("Set true to skip already split transactions.")
        .optional(),
});

/**
 * Sources a client may claim. The others are stamped by backend writers
 * (similarity engine, entity migration, loan reconciliation, platform API) and
 * change how the rule is read.
 */
const CLIENT_RULE_SOURCES: ReadonlySet<RuleSource> = new Set([
    RuleSource.SETTINGS,
    RuleSource.TRANSACTIONS_FEED,
    RuleSource.ACTIONS,
]);

export const RuleCreationSchema = z
    .object({
        rule: RuleUpdateSchema.describe(
            "Rule definition with group/order, conditions, actions, enabled state, and note.",
        ),
        source: z
            .nativeEnum(RuleSource)
            .refine((source) => CLIENT_RULE_SOURCES.has(source), {
                message: "Rule source is reserved for backend writers",
            })
            .describe("Source context for the new rule, usually settings."),
    })
    .merge(RuleExistingTransactionApplicationSchema);

export const RuleUpdateQuerySchema = z
    .object({
        rule: RuleUpdateSchema.describe("Rule fields to update."),
    })
    .merge(RuleExistingTransactionApplicationSchema);

export const RuleGroupUpdateSchema = z.object({
    name: z.string().describe("Rule group display name.").optional(),
    order: z.coerce
        .number()
        .nonnegative()
        .describe("Absolute rule group order.")
        .optional(),
    icon: z
        .string()
        .describe("Optional icon identifier for the group.")
        .optional(),
});

export const RuleGroupCreateSchema = z.object({
    name: z.string().describe("Rule group display name."),
    order: z.coerce
        .number()
        .nonnegative()
        .describe("Absolute rule group order."),
    icon: z
        .string()
        .describe("Optional icon identifier for the group.")
        .optional(),
    type: z
        .nativeEnum(RuleGroupType)
        .describe(
            "Rule group type: transaction, accounting, transfer, or split.",
        ),
});

export const RuleOrderUpdateSchema = z.object({
    order: z
        .number()
        .nonnegative()
        .describe("Absolute rule order in the group."),
    groupId: z.string().uuid().describe("Destination rule group UUIDv7."),
});

export const DeleteRuleErrorSchema = z.object({
    reversalErrorsNumber: z.number().optional(),
    processedTransactionsNumber: z.number().optional(),
});

export const AppliedTransactionsQuerySchema = z.object({
    page: z.number().int().min(0),
    limit: z.number().int().min(1).max(200),
});

export const AppliedTransferTransactionsQuerySchema =
    AppliedTransactionsQuerySchema;

export const NormalizedDescriptionsResponseSchema = z.object({
    normalizedDescriptions: z.array(
        z.object({
            normalizedDescriptionId: z.string(),
            normalizedDescription: z.string(),
            sampleBankDescription: z.string().nullable(),
        }),
    ),
    mergedToCanonicalMap: z.record(z.string(), z.string()).optional(),
});
